import i18n from 'src/lib/i18n/i18n';
import View from '@play-co/timestep-core/lib/ui/View';
import { Feature } from 'src/replicant/analytics';
import { getPuzzleMap } from '../match2.assets';
import PopupMatch2Base, { PopupMatch2BaseInitOptions } from './PopupMatch2Base';
import { Match2GoalsSummary } from './Match2GoalsSummary';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import Match2BuruStreakPanel from './Match2BuruStreakPanel';
import StateObserver from 'src/StateObserver';
import { isMatch2UnlockableUnlocked } from 'src/replicant/match2/match2.getters';
import { MovieClip, animate } from '@play-co/timestep-core/ui';
import { delay } from 'src/lib/utils';
import { app } from '../game/app';
import {
  canPlayLeague,
  getCurrentLeaguePhase,
} from 'src/replicant/match2/leagues/leagues.getters';

interface PopupMatch2LevelCompleteInitOptions extends PopupMatch2BaseInitOptions {
  level: number;
  stars?: number;
  /** One-based Dream Drift node; replaces the title level and disables streaks. */
  dreamDrift?: number;
}

export default class PopupMatch2LevelComplete extends PopupMatch2Base {
  private clipsView!: View;
  private confettiClip!: MovieClip;
  private charactersClip!: MovieClip;
  private starsClip!: MovieClip;
  private goals: Match2GoalsSummary;
  private streak?: Match2BuruStreakPanel;
  private starsCount = 3;

  constructor(opts: { superview: View; close: () => void }) {
    super({
      superview: opts.superview,
      close: async () => opts.close(),
      title: { type: 'banner' },
      cta: {
        localeText: () => i18n('match2.ui.awesome'),
        positioning: 'inside',
        width: 300,
        onClick: async () => opts.close(),
      },
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PUZZLE_COMPLETE_POPUP,
      },
    });

    this.clipsView = new View({
      superview: this.root,
      x: this.root.style.width * 0.5,
      y: 635,
      centerOnOrigin: true,
    });
    const clipOpts = {
      superview: this.clipsView,
      url: 'assets/ui/match2/movieclips/level_complete_stars',
    };
    this.charactersClip = new MovieClip({
      ...clipOpts,
      defaultAnimation: 'staranim_characters',
      scale: 0,
    });
    this.confettiClip = new MovieClip(clipOpts);
    this.starsClip = new MovieClip(clipOpts);

    this.goals = new Match2GoalsSummary({
      superview: this.panel,
      x: this.panel.style.width * 0.5,
      y: this.panel.style.height * 0.5 + 10,
    });

    new LangBitmapFontTextView({
      superview: this.panel,
      font: 'Body',
      color: '#3F9207',
      size: 38,
      x: this.panel.style.width * 0.5,
      y: 100,
      centerOnOrigin: true,
      centerAnchor: true,
      localeText: () => i18n('match2.ui.levelComplete'),
      verticalAlign: 'center',
      align: 'center',
    });
  }

  private createBuruStreakPanel() {
    this.streak = new Match2BuruStreakPanel({
      superview: this.panel,
      x: this.panel.style.width * 0.5,
      y: this.panel.style.height + 190,
    });
  }

  public async init({
    level,
    stars = 3,
    useClickOutside,
    dreamDrift,
  }: PopupMatch2LevelCompleteInitOptions) {
    const { user } = StateObserver.getState();
    // Dream Drift uses its displayed level even when regular leagues are active.
    if (!dreamDrift && canPlayLeague(user) && getCurrentLeaguePhase(user) > 1) {
      this.setTitleText(() =>
        i18n('match2.leagues.phase', {
          phase: getCurrentLeaguePhase(user) - 1,
        }).toLocaleUpperCase(),
      );
    } else {
      this.setTitleText(() =>
        i18n('match2.ui.level', {
          level: dreamDrift || level,
        }).toLocaleUpperCase(),
      );
    }

    super.init({ useClickOutside });
    this.starsCount = stars;

    // Dream Drift wins never present or animate regular puzzle streaks.
    if (
      !dreamDrift &&
      isMatch2UnlockableUnlocked(StateObserver.getState().user, 'buruStreak')
    ) {
      this.createBuruStreakPanel();
    }

    const map = await getPuzzleMap(level);
    this.goals.setGoals(map.goals, true);
  }

  private async animateStars() {
    animate(this.charactersClip)
      .clear()
      .then({ scale: 1 }, 350, animate.easeOut)
      .toPromise();
    if (this.starsCount === 3)
      delay(1300).then(() => {
        app.sound.play(`puzzle-win-confetti`);
        this.confettiClip?.play(`staranim_confetti`);
      });
    delay(500).then(() => {
      for (let index = 0; index < this.starsCount; index++) {
        delay(250 * index).then(() => app.sound.play(`sfx-star${index + 1}`));
      }
    });
    await this.starsClip?.playAsync(`staranim_${this.starsCount}_stars`);
  }

  public override async fadeIn() {
    await super.fadeIn();
    await this.animateStars();
    this.streak?.show();
  }

  public override async fadeOut() {
    this.streak?.hide?.();
    this.clipsView?.hide();
    super.fadeOut();
  }
}
